import { Suspense, lazy, useEffect, useState } from "react";
import { Canvas } from "@react-three/fiber";

const ParticleField = lazy(() => import("@/components/hero/ParticleField"));
const NetworkNodes = lazy(() => import("@/components/hero/NetworkNodes"));

/**
 * Static SVG fallback rendered when:
 *   - prefers-reduced-motion: reduce
 *   - viewport width < 768px (mobile)
 *   - WebGL fails to initialize
 */
function StaticFallback() {
    return (
        <svg
            data-testid="hero-static-fallback"
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 1200 800"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
        >
            <defs>
                <radialGradient id="bgGlow" cx="50%" cy="55%" r="60%">
                    <stop offset="0%" stopColor="#00DBFF" stopOpacity="0.10" />
                    <stop offset="55%" stopColor="#00DBFF" stopOpacity="0.02" />
                    <stop offset="100%" stopColor="#000000" stopOpacity="0" />
                </radialGradient>
                <filter id="dotGlow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="2" />
                </filter>
            </defs>

            <rect width="1200" height="800" fill="url(#bgGlow)" />

            {/* Subtle node-network impression */}
            <g stroke="#00DBFF" strokeWidth="0.5" opacity="0.18" fill="none">
                <line x1="180" y1="200" x2="380" y2="320" />
                <line x1="380" y1="320" x2="540" y2="180" />
                <line x1="540" y1="180" x2="760" y2="280" />
                <line x1="760" y1="280" x2="940" y2="200" />
                <line x1="380" y1="320" x2="500" y2="540" />
                <line x1="500" y1="540" x2="700" y2="600" />
                <line x1="700" y1="600" x2="900" y2="520" />
                <line x1="900" y1="520" x2="940" y2="200" />
                <line x1="180" y1="200" x2="220" y2="500" />
                <line x1="220" y1="500" x2="500" y2="540" />
            </g>
            <g fill="#6EF7FF" opacity="0.85" filter="url(#dotGlow)">
                {[
                    [180, 200],
                    [380, 320],
                    [540, 180],
                    [760, 280],
                    [940, 200],
                    [220, 500],
                    [500, 540],
                    [700, 600],
                    [900, 520],
                ].map(([cx, cy], i) => (
                    <circle key={i} cx={cx} cy={cy} r="2.4">
                        <animate attributeName="opacity" values="0.5;1;0.5" dur={`${3 + (i % 3)}s`} repeatCount="indefinite" />
                    </circle>
                ))}
            </g>
        </svg>
    );
}

export default function HeroCanvas() {
    const [degraded, setDegraded] = useState(false);

    useEffect(() => {
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const small = window.matchMedia("(max-width: 767px)").matches;
        // Quick WebGL probe
        let webglOk = true;
        try {
            const c = document.createElement("canvas");
            const gl = c.getContext("webgl2") || c.getContext("webgl");
            webglOk = !!gl;
        } catch {
            webglOk = false;
        }
        if (reduced || small || !webglOk) setDegraded(true);
    }, []);

    if (degraded) return <StaticFallback />;

    return (
        <Canvas
            data-testid="hero-r3f-canvas"
            dpr={[1, 1.5]}
            gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
            camera={{ position: [0, 0, 9], fov: 55 }}
            style={{ position: "absolute", inset: 0 }}
        >
            {/* Atmospheric fog hides the back of the field */}
            <fog attach="fog" args={["#000000", 8, 22]} />
            <Suspense fallback={null}>
                <ParticleField count={520} spread={24} />
                <NetworkNodes nodeCount={32} connectDistance={6.2} />
            </Suspense>
        </Canvas>
    );
}
